import React from 'react';
import HiddenFileInput from 'components/files/HiddenFileInput';

function map(item, apiEndpoint, apiParams)
{
    return {...item, id: item.fileID, src: apiEndpoint.fileDownloadURL(...apiParams, item.fileID)}
}

async function getData(apiEndpoint, apiParams, abortSignal)
{
    let data = [];
    let indexed = {};
    let response = await apiEndpoint.getAll(...apiParams, abortSignal);
    if(response && response.ok)
    {
        data = await response.json();
        data = data.map((item)=>map(item, apiEndpoint, apiParams));
        data.forEach(item => {
            indexed[item.fileID] = item
        });
    }
    return {array: data, indexed: indexed};
}

const FilesCtx = React.createContext({
    loading: true, 
    data: {
        array: [], 
        indexed: {}
    }, 
    funcs: {
        upload: () => {},
        update: () => {},
        delete: () => {},
        internal: {
            add: (fileID, data) => {},
            update: (fileID, data) => {},
            delete: (fileID) => {},
            refresh: (fileID) => {}
        }
    }
});

function FilesCtxProvider({apiEndpoint, apiParams, ...props})
{
    const fileInput = React.useRef();
    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState({array: [], indexed: []});

    React.useEffect(function loadData() {

        // sanity check
        if(Array.isArray(apiParams)) for(let i=0;i<apiParams.length;i++)
        {
            if(apiParams[i] === null) return;
        }
        // console.log(apiParams);
        // console.trace();

        setLoading(true);
        let abController = new AbortController();

        getData(apiEndpoint, apiParams, abController.signal).then(values => {
            setData(values);
            setLoading(false);
        });

        return () => {
            abController.abort();
        }
    }, [apiEndpoint, apiParams]);

    const internalUpdate = React.useCallback((fileID, fileData) => {
        let tmp = {...data};
        Object.keys(fileData).forEach((key) => {
            tmp.indexed[fileID][key] = fileData[key];
        })
        setData(tmp);
    }, [data]);

    const internalAdd = React.useCallback(async (fileID, item) => {
        // ignore duplicated add
        if(data.indexed[fileID] !== undefined) return internalUpdate(fileID, map(item, apiEndpoint, apiParams));

        let tmp = {...data};
        item = map(item, apiEndpoint, apiParams);
        let index = tmp.array.push(item);
        tmp.indexed[item.id] = tmp.array[index-1];

        setData(tmp);
    }, [data, apiEndpoint, apiParams, internalUpdate]);

    const internalRefresh = React.useCallback(async (fileID) => {

        let result = await apiEndpoint.getSingle(...apiParams, fileID);
        if(result.ok)
        {
            let item = map(await result.json(), apiEndpoint, apiParams);
            internalUpdate(fileID, item);
            
        }
    }, [apiEndpoint, apiParams, internalUpdate]);

    const internalDelete = React.useCallback((fileID) => {
        let tmp = {...data};
        let item = tmp.indexed[fileID];

        // remove from array
        let index = tmp.array.indexOf(item);
        if(index > -1) tmp.array.splice(index,1);

        delete tmp.indexed[fileID];
        setData(tmp);
    }, [data]);


    const upload = React.useCallback(async () => {
        fileInput.current.click();
    }, [fileInput])

    const update = React.useCallback(async (fileID, data) => {
        let result = await apiEndpoint.updateSingle(...apiParams, fileID, data);
        if(result.ok) internalUpdate(fileID, data);

    }, [internalUpdate, apiEndpoint, apiParams]);

    const remove = React.useCallback(async (fileID) => {
        let result = await apiEndpoint.deleteSingle(...apiParams, fileID);
        if(result.ok) internalDelete(fileID);
    }, [internalDelete, apiEndpoint, apiParams]);

    return <FilesCtx.Provider value={{loading: loading, data: {...data}, funcs: {upload: upload, update: update, delete: remove, internal: {add: internalAdd, delete: internalDelete, update: internalUpdate, refresh: internalRefresh}}}} >
        <HiddenFileInput ref={fileInput} apiFileEndpoint={apiEndpoint} apiRequestParams={apiParams} onUpload={(fileData) => internalAdd(fileData.fileID, fileData)}/>
        {props.children}
        </FilesCtx.Provider>
}

export {FilesCtx, FilesCtxProvider};